// src/components/inspectionBot/InspectionBotPage.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SignInHeader from '../auth/SigninHeader';
import StepperHeader from '../layout/StepperHeader';
import StepperFooter from '../layout/StepperFooter';

// Import the main content components (cleaned versions without headers)
import TemplateViewerContent from './TemplateViewer';
import RecordTypeSelectorContent from './RecordTypeSelector';
import TriggerLocationContent from './TriggerLocation';
import InspectionTypeContent from './InspectionType';
import InspectionResultContent from './InspectionResult';
import ContactTypesContent from './ContactTypes';
import SummaryContent from './Summary';

const InspectionBotPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [user, setUser] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Determine current step based on route
  const getCurrentStep = () => {
    const path = location.pathname;
    if (path.includes('/template-viewer')) return 1;
    if (path.includes('/record-types')) return 2;
    if (path.includes('/triggerlocation')) return 3;
    if (path.includes('/taskselection')) return 4;
    if (path.includes('/inspection-result')) return 5;
    if (path.includes('/contact-types')) return 6;
    if (path.includes('/summary')) return 7;
    return 1; // default
  };

  const currentStep = getCurrentStep();

  // Step configuration
  const stepConfig = {
    1: { title: 'Select Template', component: TemplateViewerContent },
    2: { title: 'Record Type', component: RecordTypeSelectorContent },
    3: { title: 'Trigger Location', component: TriggerLocationContent },
    4: { title: 'Inspection Type', component: InspectionTypeContent },
    5: { title: 'Inspection Result', component: InspectionResultContent },
    6: { title: 'Contact Types', component: ContactTypesContent },
    7: { title: 'Summary', component: SummaryContent },
  };

  // Load theme and user data
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(savedTheme === 'dark' || (!savedTheme && prefersDark));

    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Error parsing stored user:', e);
      }
    }

    const token = localStorage.getItem('token');
    setAuthenticated(!!token);

    if (!token) {
      navigate('/signin');
      return;
    }
  }, [navigate]);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
  };

  const handleBack = () => {
    const backRoutes = {
      2: '/mainscreen/template-viewer',
      3: '/mainscreen/record-types',
      4: '/mainscreen/triggerlocation',
      5: '/mainscreen/taskselection',
      6: '/mainscreen/inspection-result',
      7: '/mainscreen/contact-types'
    };
    
    if (currentStep === 1) {
      navigate('/mainscreen');
    } else {
      navigate(backRoutes[currentStep]);
    }
  };

  const handleNext = () => {
    const nextRoutes = {
      1: '/mainscreen/record-types',
      2: '/mainscreen/triggerlocation',
      3: '/mainscreen/taskselection',
      4: '/mainscreen/inspection-result',
      5: '/mainscreen/contact-types',
      6: '/mainscreen/summary'
    };
    
    if (nextRoutes[currentStep]) {
      navigate(nextRoutes[currentStep]);
    }
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      console.log('Draft saved at step:', currentStep);
    } catch (error) {
      console.error('Save draft error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Get the current step's component
  const CurrentStepComponent = stepConfig[currentStep]?.component || TemplateViewerContent;

  return (
    <div className={`h-screen overflow-hidden transition-all duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
    }`} style={{ fontFamily: "'Inter', Helvetica, Arial, sans-serif" }}>
      
      {/* SignIn Header */}
      <SignInHeader
        user={user}
        isLoggedIn={authenticated}
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
      />

      {/* Stepper Header */}
      <StepperHeader
        title="Inspection Bot"
        currentStep={currentStep}
        showSaveDraft={true}
        onSaveDraft={handleSaveDraft}
        isDarkMode={isDarkMode}
        isSaving={isSaving}
        toggleDarkMode={toggleDarkMode}
      />

      {/* Main Content Area */}
      <main className={`fixed top-[174px] left-0 right-0 bottom-[80px] overflow-hidden ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="max-w-[1200px] mx-auto px-8 h-full">
          {/* Render the current step's content component */}
          <CurrentStepComponent
            isDarkMode={isDarkMode}
            onNext={handleNext}
            onBack={handleBack}
            currentStep={currentStep}
          />
        </div>
      </main>

      {/* Stepper Footer */}
      <div className={`fixed bottom-0 left-0 right-0 ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <StepperFooter
          currentStep={currentStep}
          totalSteps={7}
          onBack={handleBack}
          onNext={handleNext}
          nextDisabled={false} // This will be controlled by each content component
          backDisabled={false}
          nextLabel={currentStep === 7 ? "Finish" : "Next"}
          backLabel="Back"
          nextLoading={false}
          isDarkMode={isDarkMode}
        />
      </div>
    </div>
  );
};

export default InspectionBotPage;